// src/pages/ClientDetails.jsx
// 🔥 V6.0 ENTERPRISE: Observer real (_listeners) + Modal portalizado + ErrorBoundary por tab
// ✅ Documentos: SIN polling manual (documentsKey eliminado). Sync vía ctx._listeners (state completo).
// ✅ DocumentList recibe `documents` como prop (componente tonto) + onDocumentChange opcional.
// ✅ Header y título 100% dark-mode compatible (Tailwind dark: + var(--primary-color)).
// ✅ Mobile: tabs inferiores tipo app nativa, touch-friendly, feedback háptico.
// ✅ Desktop: layout editorial premium, glassmorphism sutil, breadcrumb.
// ✅ Anti-crash: mountedRef, try/catch en todo async, timeout 8s, fallbacks en cascada.
// ✅ Modal de vinculação renderizado via portal (portalUtils) — nunca queda atrás do header.
// ✅ Cada tab envolta em TabErrorBoundary — um crash não derruba a página inteira.
// ✅ DataContext.getInstance() async — misma arquitectura previa.
// ✅ Vinculación de propiedades, modal, notas, exports y props: SIN CAMBIOS de lógica.
import React, { useEffect, useMemo, useState, useCallback, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  Phone,
  ArrowLeft,
  Edit,
  Trash2,
  Tag,
  Link,
  Download,
  MessageCircle,
  Thermometer,
  Target,
  Home,
  Mail,
  Copy,
  Clock,
  MapPin,
  X,
  Search,
  Plus,
  Building2,
  HelpCircle,
  FileText,
  Camera,
  Image as ImageIcon,
  RefreshCw,
  AlertCircle,
  ChevronRight
} from "lucide-react";
import { useAuth } from "../contexts/useAuth";
import { useIsMobile } from "../hooks/useMediaQuery";
import { ClientMessageHistory } from "../features/mensagens/components/ClientMessageHistory";
import { ClientNotes } from "../components/ClientNotes";
import { ScoreProgress } from "../components/ScoreProgress";
import { DocumentList } from "../components/DocumentList";
import { Timeline } from "../components/Timeline";
import { SmartTags } from "../components/SmartTags";
import { useClientDetailsShortcuts } from "../hooks/useKeyboardShortcuts.jsx";
import { usePortal, useBodyScrollLock, useEscapeKey, portalize } from "../utils/portalUtils";
// 🔥 DataContext como fuente única de verdad
import DataContext from "../lib/data/context/DataContext";

/* ======================================================
  🏆 CLIENTDETAILS — V6.0 ENTERPRISE
  ✅ Documentos en tiempo real (observer, sin polling)
  ✅ UI ultra moderna, dark mode full, mobile nativo
  ✅ Sistema anti-crash con timeout 8s y fallback en cascada
====================================================== */

// ==========================================================
// CONSTANTES E CONFIGURAÇÕES (FORA DO COMPONENTE)
// ==========================================================

const TABS = [
  { id: 'timeline', label: 'Linha do Tempo', icon: Clock, mobileLabel: 'Timeline' },
  { id: 'notes', label: 'Notas', icon: FileText, mobileLabel: 'Notas' },
  { id: 'documents', label: 'Documentos', icon: Download, mobileLabel: 'Docs' },
  { id: 'messages', label: 'Mensagens', icon: MessageCircle, mobileLabel: 'Msgs' }
];

// Timeout máximo para cualquier operación async crítica (anti-crash)
const ASYNC_TIMEOUT_MS = 8000;

const getPipelineColor = (pipeline) => {
  switch (pipeline) {
    case "Lead Novo":
      return "text-blue-700 bg-blue-50 border-blue-200 dark:text-blue-300 dark:bg-blue-500/10 dark:border-blue-500/30";
    case "Em Atendimento":
      return "text-amber-700 bg-amber-50 border-amber-200 dark:text-amber-300 dark:bg-amber-500/10 dark:border-amber-500/30";
    case "Proposta Enviada":
      return "text-purple-700 bg-purple-50 border-purple-200 dark:text-purple-300 dark:bg-purple-500/10 dark:border-purple-500/30";
    case "Fechado":
      return "text-emerald-700 bg-emerald-50 border-emerald-200 dark:text-emerald-300 dark:bg-emerald-500/10 dark:border-emerald-500/30";
    default:
      return "text-secondary bg-muted border-color dark:text-slate-300 dark:bg-muted0/10 dark:border-slate-500/30";
  }
};

const getTemperatureColor = (temp) => {
  switch (temp) {
    case "Quente":
      return "text-rose-700 bg-rose-50 border-rose-200 dark:text-rose-300 dark:bg-rose-500/10 dark:border-rose-500/30";
    case "Morno":
      return "text-amber-700 bg-amber-50 border-amber-200 dark:text-amber-300 dark:bg-amber-500/10 dark:border-amber-500/30";
    case "Frio":
      return "text-blue-700 bg-blue-50 border-blue-200 dark:text-blue-300 dark:bg-blue-500/10 dark:border-blue-500/30";
    default:
      return "text-secondary bg-muted border-color dark:text-slate-300 dark:bg-muted0/10 dark:border-slate-500/30";
  }
};

const safeStr = (v, fallback = "-") => {
  if (v === null || v === undefined) return fallback;
  if (typeof v === 'string') {
    const trimmed = v.trim();
    return trimmed.length > 0 ? trimmed : fallback;
  }
  if (typeof v === 'number') return String(v);
  return fallback;
};

const onlyDigits = (v) => safeStr(v, "").replace(/\D/g, "");

const formatDateBR = (d = new Date()) => {
  try {
    return new Date(d).toLocaleDateString("pt-BR");
  } catch {
    return "";
  }
};

const formatLastContact = (date) => {
  if (!date) return null;

  const now = new Date();
  const last = new Date(date);
  const diffDays = Math.floor((now - last) / (1000 * 60 * 60 * 24));

  if (diffDays === 0) return "Hoje";
  if (diffDays === 1) return "Ontem";
  if (diffDays < 7) return `${diffDays} dias atrás`;
  if (diffDays < 30) return `${Math.floor(diffDays / 7)} semana(s) atrás`;
  return formatDateBR(date);
};

const getInitials = (name) => {
  const s = safeStr(name, "");
  if (!s || s === "-") return "?";
  return s
    .split(' ')
    .filter(Boolean)
    .map((w) => w[0])
    .slice(0, 2)
    .join('')
    .toUpperCase();
};

// Anti-crash: envuelve cualquier promesa con un timeout máximo
const withTimeout = (promise, ms = ASYNC_TIMEOUT_MS, label = 'operação') => {
  return Promise.race([
    promise,
    new Promise((_, reject) =>
      setTimeout(() => reject(new Error(`Timeout: ${label} excedeu ${ms}ms`)), ms)
    ),
  ]);
};

const PROPERTIES_CACHE_TTL = 5 * 60 * 1000;

// Haptic feedback (no-op seguro si el navegador/dispositivo no lo soporta)
const triggerHaptic = (pattern = 10) => {
  try {
    if (typeof navigator !== 'undefined' && navigator.vibrate) {
      navigator.vibrate(pattern);
    }
  } catch {
    /* silencioso: haptics es un nice-to-have, nunca debe romper la UI */
  }
};

// ==========================================================
// COMPONENTES AUXILIARES (MEMOIZADOS)
// ==========================================================

const Tooltip = React.memo(({ children, content, position = 'top' }) => {
  const [show, setShow] = useState(false);
  const isMobile = useIsMobile();

  if (isMobile) return children;

  return (
    <div
      className="relative inline-block"
      onMouseEnter={() => setShow(true)}
      onMouseLeave={() => setShow(false)}
    >
      {children}
      <AnimatePresence>
        {show && (
          <motion.div
            initial={{ opacity: 0, y: position === 'top' ? 10 : -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: position === 'top' ? 10 : -10 }}
            style={{ willChange: 'transform, opacity' }}
            className={`absolute z-50 px-2 py-1 text-xs text-white bg-slate-900 dark:bg-slate-700 rounded whitespace-nowrap shadow-lg ${
              position === 'top' ? 'bottom-full left-1/2 -translate-x-1/2 mb-2' : 'top-full left-1/2 -translate-x-1/2 mt-2'
            }`}
          >
            {content}
            <div className={`absolute ${
              position === 'top'
                ? 'top-full left-1/2 -translate-x-1/2 border-t-slate-900 dark:border-t-slate-700'
                : 'bottom-full left-1/2 -translate-x-1/2 border-b-slate-900 dark:border-b-slate-700'
            } border-4 border-transparent`} />
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
});
Tooltip.displayName = 'Tooltip';

// 🛡️ V6.0: Error boundary por pestaña — un crash en Timeline/Notes/Documents/Messages
// no debe derribar toda la página de ClientDetails. Cada tab queda aislada.
const TabErrorBoundary = ({ children, tabName }) => {
  const [hasError, setHasError] = useState(false);

  if (hasError) {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-muted dark:text-muted gap-2">
        <AlertCircle className="w-8 h-8 text-rose-400 dark:text-rose-400" />
        <p className="text-sm">Erro ao carregar {tabName}</p>
        <button onClick={() => setHasError(false)} className="text-blue-600 dark:text-blue-400 text-sm hover:underline">
          Tentar novamente
        </button>
      </div>
    );
  }

  return children;
};

// 🎨 Skeleton loader ultra moderno (dark-mode compatible, shimmer GPU-accelerated)
const ShimmerLoader = React.memo(() => {
  const isMobile = useIsMobile();

  return (
    <div className={isMobile ? "space-y-3" : "space-y-4"}>
      <div className={`relative overflow-hidden ${isMobile ? 'h-6 w-32' : 'h-8 w-48'} bg-muted dark:bg-slate-800 rounded-lg`} style={{ willChange: 'transform' }}>
        <div className="absolute inset-0 -translate-x-full animate-[shimmer_1.5s_infinite] bg-gradient-to-r from-transparent via-white/40 dark:via-white/10 to-transparent" />
      </div>
      <div className={`relative overflow-hidden ${isMobile ? 'h-20' : 'h-28'} bg-muted dark:bg-slate-800 rounded-2xl`} style={{ willChange: 'transform' }}>
        <div className="absolute inset-0 -translate-x-full animate-[shimmer_1.5s_infinite] bg-gradient-to-r from-transparent via-white/40 dark:via-white/10 to-transparent" />
      </div>
      <div className={`grid ${isMobile ? 'grid-cols-1 gap-3' : 'grid-cols-1 md:grid-cols-3 gap-4'}`}>
        {[1, 2, 3].map((i) => (
          <div key={i} className={`relative overflow-hidden ${isMobile ? 'h-24' : 'h-32'} bg-muted dark:bg-slate-800 rounded-xl`} style={{ willChange: 'transform' }}>
            <div className="absolute inset-0 -translate-x-full animate-[shimmer_1.5s_infinite] bg-gradient-to-r from-transparent via-white/40 dark:via-white/10 to-transparent" />
          </div>
        ))}
      </div>
    </div>
  );
});
ShimmerLoader.displayName = 'ShimmerLoader';

// 🎨 Estado de error con animación (anti-crash UX)
const ErrorState = React.memo(({ message, onRetry }) => (
  <motion.div
    initial={{ opacity: 0, scale: 0.96 }}
    animate={{ opacity: 1, scale: 1 }}
    transition={{ duration: 0.25 }}
    className="flex flex-col items-center justify-center text-center py-16 px-4"
  >
    <motion.div
      initial={{ rotate: -8 }}
      animate={{ rotate: [0, -6, 6, -4, 4, 0] }}
      transition={{ duration: 0.6, delay: 0.1 }}
      className="w-16 h-16 rounded-2xl bg-rose-50 dark:bg-rose-500/10 flex items-center justify-center mb-4"
    >
      <AlertCircle className="w-8 h-8 text-rose-500 dark:text-rose-400" />
    </motion.div>
    <h3 className="text-lg font-semibold text-primary dark:text-white mb-1">
      Não foi possível carregar
    </h3>
    <p className="text-sm text-muted dark:text-muted max-w-xs mb-4">
      {message || 'Ocorreu um erro inesperado. Tente novamente.'}
    </p>
    {onRetry && (
      <motion.button
        whileHover={{ scale: 1.03 }}
        whileTap={{ scale: 0.97 }}
        onClick={onRetry}
        className="px-4 py-2 rounded-xl bg-slate-900 dark:bg-card text-white dark:text-primary text-sm font-medium inline-flex items-center gap-2"
      >
        <RefreshCw className="w-4 h-4" />
        Tentar novamente
      </motion.button>
    )}
  </motion.div>
));
ErrorState.displayName = 'ErrorState';

const PropertyCard = React.memo(({ property, onView, onUnlink, index, companyCor }) => {
  const isMobile = useIsMobile();

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: Math.min(index * 0.05, 0.3) }}
      whileHover={{ y: isMobile ? 0 : -4 }}
      style={{ willChange: 'transform, opacity', transform: 'translateZ(0)' }}
      className="group relative bg-card dark:bg-slate-800/60 rounded-xl border border-color dark:border-slate-700/60 overflow-hidden hover:shadow-xl dark:hover:shadow-black/30 transition-shadow duration-300"
    >
      <div
        className="absolute top-0 left-0 right-0 h-1"
        style={{ backgroundColor: companyCor }}
      />

      <div className="aspect-video bg-gradient-to-br from-slate-100 to-slate-200 dark:from-slate-700 dark:to-slate-800 relative overflow-hidden">
        {property.previewImage ? (
          <img
            src={property.previewImage}
            alt={property.title}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
            loading="lazy"
          />
        ) : property.images?.length > 0 ? (
          <>
            <div className="absolute inset-0 flex items-center justify-center">
              <ImageIcon className="w-8 h-8 text-muted dark:text-muted" />
            </div>
            <div className="absolute bottom-2 right-2 flex items-center gap-1 px-2 py-1 bg-black/60 text-white text-xs rounded-full backdrop-blur-sm">
              <Camera className="w-3 h-3" />
              <span>{property.images.length}</span>
            </div>
          </>
        ) : (
          <div className="absolute inset-0 flex items-center justify-center">
            <Building2 className="w-8 h-8 text-muted dark:text-muted" />
          </div>
        )}

        <span className="absolute top-2 right-2 px-2 py-1 text-xs bg-card/90 dark:bg-slate-900/80 text-secondary dark:text-slate-200 backdrop-blur-sm rounded-full shadow-sm border border-color dark:border-slate-700">
          {property.status || "Disponível"}
        </span>
      </div>

      <div className={isMobile ? 'p-3' : 'p-4'}>
        <h3 className={`font-semibold text-primary dark:text-white truncate ${isMobile ? 'text-sm' : ''}`}>
          {property.title || "Imóvel sem título"}
        </h3>
        <div className="flex items-center gap-1 text-xs text-muted dark:text-muted mt-1">
          <MapPin className="w-3 h-3 flex-shrink-0" />
          <span className="truncate">
            {property.neighborhood} {property.city && `• ${property.city}`}
          </span>
        </div>

        {property.price && (
          <div className="mt-2">
            <span className={`font-bold text-emerald-600 dark:text-emerald-400 ${isMobile ? 'text-base' : 'text-lg'}`}>
              {new Intl.NumberFormat('pt-BR', {
                style: 'currency',
                currency: 'BRL',
                maximumFractionDigits: 0
              }).format(property.price)}
            </span>
          </div>
        )}

        <div className={`flex gap-2 mt-4 ${isMobile ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'} transition-opacity duration-200`}>
          <motion.button
            whileHover={{ scale: isMobile ? 1 : 1.02 }}
            whileTap={{ scale: 0.98 }}
            onClick={() => onView(property.id)}
            className={`flex-1 bg-muted dark:bg-slate-700 text-secondary dark:text-slate-200 rounded-lg hover:bg-muted dark:hover:bg-slate-600 font-medium transition-colors ${
              isMobile ? 'px-2 py-1.5 text-xs' : 'px-3 py-2 text-sm'
            }`}
          >
            Ver
          </motion.button>
          <Tooltip content="Desvincular imóvel">
            <motion.button
              whileHover={{ scale: isMobile ? 1 : 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => onUnlink(property.id)}
              className={`bg-rose-50 dark:bg-rose-500/10 text-rose-600 dark:text-rose-400 rounded-lg hover:bg-rose-100 dark:hover:bg-rose-500/20 transition-colors ${
                isMobile ? 'p-1.5' : 'p-2'
              }`}
            >
              <Trash2 className={isMobile ? 'w-3 h-3' : 'w-4 h-4'} />
            </motion.button>
          </Tooltip>
        </div>
      </div>
    </motion.div>
  );
}, (prevProps, nextProps) => {
  return prevProps.property.id === nextProps.property.id &&
         prevProps.property.title === nextProps.property.title &&
         prevProps.property.price === nextProps.property.price &&
         prevProps.property.status === nextProps.property.status &&
         prevProps.property.previewImage === nextProps.property.previewImage &&
         prevProps.companyCor === nextProps.companyCor;
});
PropertyCard.displayName = 'PropertyCard';

// ==========================================================
// COMPONENTE PRINCIPAL — V6.0 ENTERPRISE
// ==========================================================
export default function ClientDetails() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const { database, openConfirm, showToast, addNotification } = useAuth();

  // userId desde auth context o localStorage
  const userId = useMemo(() => {
    try {
      const stored = localStorage.getItem('auth_user');
      return stored ? JSON.parse(stored).id : null;
    } catch {
      return null;
    }
  }, []);

  // primaryColor desde CSS custom property (white-label do sistema)
  const primaryColor = useMemo(() => {
    try {
      return getComputedStyle(document.documentElement)
        .getPropertyValue('--primary-color').trim() || '#2563eb';
    } catch {
      return '#2563eb';
    }
  }, []);

  const primaryLight = `${primaryColor}20`;

  // DataContext ASYNC — instancia real con useRef + estado ready
  const dataContextRef = useRef(null);
  const [ctxReady, setCtxReady] = useState(false);

  // 🛡️ ANTI-CRASH: mountedRef para evitar setState após desmontagem
  const mountedRef = useRef(true);

  // Referencias frescas para evitar stale closure en callbacks/observers
  const clientRef = useRef(null);
  const linkedPropertiesRef = useRef([]);
  const clientDocumentsRef = useRef([]);
  const observerCleanupRef = useRef(null);

  // getDataContext ASYNC — espera la instancia real, con timeout anti-crash
  const getDataContext = useCallback(async () => {
    if (dataContextRef.current?.collections) {
      return dataContextRef.current;
    }
    if (!userId) return null;

    try {
      const ctx = await withTimeout(
        DataContext.getInstance(userId),
        ASYNC_TIMEOUT_MS,
        'inicialização do DataContext'
      );
      if (ctx?.collections) {
        dataContextRef.current = ctx;
        if (mountedRef.current && !ctxReady) setCtxReady(true);
        return ctx;
      }
    } catch (e) {
      console.error('❌ [V6.0] Error obteniendo DataContext:', e.message);
    }
    return null;
  }, [userId, ctxReady]);

  // Inicializar DataContext al montar
  useEffect(() => {
    mountedRef.current = true;

    getDataContext()
      .then((ctx) => {
        if (!mountedRef.current) return;
        if (ctx) console.log('✅ [V6.0] ClientDetails: DataContext inicializado');
        else console.warn('⚠️ [V6.0] ClientDetails: DataContext no disponible');
        setCtxReady(true);
      })
      .catch(() => {
        if (mountedRef.current) setCtxReady(true);
      });

    return () => {
      mountedRef.current = false;
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const [client, setClient] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const [linkedProperties, setLinkedProperties] = useState([]);
  const [availableProperties, setAvailableProperties] = useState([]);
  const [activeTab, setActiveTab] = useState('timeline');
  const [loading, setLoading] = useState(true);
  const [showLinkModal, setShowLinkModal] = useState(false);
  const [modalSearchTerm, setModalSearchTerm] = useState('');
  const [showTips, setShowTips] = useState(!isMobile);
  const [loadingPreviews, setLoadingPreviews] = useState(false);
  const [modalDiagnostic, setModalDiagnostic] = useState(null);
  const [propertiesCache, setPropertiesCache] = useState({
    timestamp: null,
    available: []
  });

  // 🔥 V6.0: Documentos del cliente — SIN polling, alimentado por observer
  const [clientDocuments, setClientDocuments] = useState([]);
  const [documentsLoading, setDocumentsLoading] = useState(true);
  const [documentsError, setDocumentsError] = useState(null);

  const headerRef = useRef(null);
  const contentRef = useRef(null);

  // 🔥 V6.0: Portal para el modal de vinculación — evita que quede atrás del header sticky
  const linkModalMounted = usePortal();
  useBodyScrollLock(showLinkModal);
  useEscapeKey(showLinkModal, () => setShowLinkModal(false));

  // Mantener referencias frescas sincronizadas (evita stale closures en el observer)
  useEffect(() => {
    clientRef.current = client;
  }, [client]);

  useEffect(() => {
    linkedPropertiesRef.current = linkedProperties;
  }, [linkedProperties]);

  useEffect(() => {
    clientDocumentsRef.current = clientDocuments;
  }, [clientDocuments]);

  // Company desde DataContext o fallback
  const company = useMemo(() => {
    try {
      const ctx = dataContextRef.current;
      if (ctx?.currentUser) {
        return {
          nome: ctx.currentUser.name || "Minha Empresa",
          cor: primaryColor,
          slogan: "Sua imobiliária digital"
        };
      }
    } catch {
      /* fallback abaixo */
    }
    return database?.getCompany?.() || {
      nome: "Minha Empresa",
      cor: primaryColor,
      slogan: "Sua imobiliária digital"
    };
  }, [database, primaryColor]);

  // Obtener propiedades desde DataContext (async, con fallback en cascada)
  const getProperties = useCallback(async () => {
    try {
      const ctx = await getDataContext();
      if (ctx?.collections?.properties?.read) {
        const props = await withTimeout(ctx.collections.properties.read(), ASYNC_TIMEOUT_MS, 'leitura de imóveis');
        if (Array.isArray(props) && props.length > 0) {
          return props;
        }
      }
    } catch (e) {
      console.warn('⚠️ [V6.0] DataContext no disponible para getProperties:', e.message);
    }
    // Fallback a database.js
    try {
      const props = await database?.getProperties?.();
      if (Array.isArray(props)) return props;
    } catch (e) {
      console.warn('⚠️ [V6.0] database.js fallback falhou em getProperties:', e?.message);
    }
    return [];
  }, [database, getDataContext]);

  // Obtener cliente por ID desde DataContext (async, con fallback en cascada)
  const getClientById = useCallback(async (clientId) => {
    try {
      const ctx = await getDataContext();
      if (ctx?.collections?.clients?.read) {
        const result = await withTimeout(
          ctx.collections.clients.read({ id: clientId }),
          ASYNC_TIMEOUT_MS,
          'leitura de cliente'
        );
        if (Array.isArray(result) && result.length > 0) return result[0];
        if (result && !Array.isArray(result)) return result;
      }
    } catch (e) {
      console.warn('⚠️ [V6.0] DataContext no disponible para getClientById:', e.message);
    }
    try {
      return database?.getClientById?.(clientId) || null;
    } catch (e) {
      console.warn('⚠️ [V6.0] database.js fallback falhou em getClientById:', e?.message);
      return null;
    }
  }, [database, getDataContext]);

  // Obtener preview de imagen
  const getPropertyPreview = useCallback(async (property) => {
    if (property.previewImage) return property.previewImage;
    if (property.images?.length > 0) {
      if (property.images[0]?.startsWith('data:')) return property.images[0];
      if (typeof database?.getPropertyPreviewImage === 'function') {
        try {
          return await withTimeout(
            database.getPropertyPreviewImage(property.id),
            ASYNC_TIMEOUT_MS,
            'preview de imóvel'
          );
        } catch {
          return null;
        }
      }
    }
    return null;
  }, [database]);

  // 🔥 V6.0: Obtener documentos del cliente desde DataContext (async, fallback en cascada)
  // Fuente única de verdad para la pestaña Documentos. NO usa polling ni window events:
  // se invoca en la carga inicial y cada vez que el observer detecta un cambio relevante.
  const getClientDocuments = useCallback(async (clientId) => {
    if (!clientId) return [];

    // 1) DataContext (fuente primaria)
    try {
      const ctx = await getDataContext();
      if (ctx?.collections?.documents?.read) {
        const docs = await withTimeout(
          ctx.collections.documents.read(),
          ASYNC_TIMEOUT_MS,
          'leitura de documentos'
        );
        if (Array.isArray(docs)) {
          return docs.filter((d) => d.clientId === clientId);
        }
      }
    } catch (e) {
      console.warn('⚠️ [V6.0] DataContext no disponible para getClientDocuments:', e.message);
    }

    // 2) Fallback: database.js
    try {
      if (typeof database?.getDocumentsByClient === 'function') {
        const docs = await database.getDocumentsByClient(clientId);
        if (Array.isArray(docs)) return docs;
      }
      if (typeof database?.getDocuments === 'function') {
        const docs = await database.getDocuments();
        if (Array.isArray(docs)) return docs.filter((d) => d.clientId === clientId);
      }
    } catch (e) {
      console.warn('⚠️ [V6.0] database.js fallback falhou em getClientDocuments:', e?.message);
    }

    // 3) Fallback: localStorage (último recurso, nunca dejar la UI sin datos posibles)
    try {
      const raw = localStorage.getItem('imoveispro_documents');
      if (raw) {
        const all = JSON.parse(raw);
        if (Array.isArray(all)) return all.filter((d) => d.clientId === clientId);
      }
    } catch {
      /* silencioso: es el último fallback, si falla simplemente no hay documentos */
    }

    return [];
  }, [database, getDataContext]);

  // 🔥 V6.0: Refrescar documentos de forma aislada (no bloquea el resto de la UI)
  const refreshClientDocuments = useCallback(async () => {
    const currentClient = clientRef.current;
    if (!currentClient?.id) return;

    try {
      const docs = await getClientDocuments(currentClient.id);
      if (mountedRef.current) {
        setClientDocuments(docs);
        setDocumentsError(null);
      }
    } catch (e) {
      console.error('❌ [V6.0] Erro ao atualizar documentos:', e.message);
      if (mountedRef.current) {
        setDocumentsError(e.message || 'Erro ao carregar documentos');
      }
    } finally {
      if (mountedRef.current) setDocumentsLoading(false);
    }
  }, [getClientDocuments]);

  // ==========================================================================
  // 🔥 V6.0: CARGA INICIAL (ASYNC CORRECTO, CON TIMEOUT Y FALLBACK)
  // ==========================================================================
  useEffect(() => {
    if (!ctxReady) return;

    const loadClientData = async () => {
      if (!mountedRef.current) return;
      setLoading(true);
      setLoadError(null);

      try {
        const found = await getClientById(id);

        if (!mountedRef.current) return;

        if (!found) {
          showToast('Cliente não encontrado', 'warning');
          navigate("/clientes");
          return;
        }

        // Cargar propiedades vinculadas
        const props = await getProperties();
        let linked = props.filter((p) =>
          p.clientId === found.id ||
          (p.linkedClients && p.linkedClients.includes(found.id))
        );

        // Cargar previsualizaciones de imágenes
        if (linked.length > 0) {
          if (mountedRef.current) setLoadingPreviews(true);

          try {
            const linkedWithPreviews = await Promise.all(
              linked.map(async (prop) => {
                const preview = await getPropertyPreview(prop);
                return {
                  ...prop,
                  previewImage: preview,
                  hasImages: !!(preview || prop.images?.length > 0),
                  imageCount: prop.images?.length || 0
                };
              })
            );
            linked = linkedWithPreviews;
          } catch (error) {
            console.error('❌ Erro ao carregar previews:', error);
          } finally {
            if (mountedRef.current) setLoadingPreviews(false);
          }
        }

        if (!mountedRef.current) return;

        console.log("🔍 [V6.0] ClientDetails - cliente cargado:", found.name, "tags:", found.tags, "props:", linked.length);

        setClient({
          ...found,
          tags: found.tags || []
        });
        setLinkedProperties(linked);

        // 🔥 V6.0: Carga inicial de documentos (única vez al montar, luego vive del observer)
        setDocumentsLoading(true);
        try {
          const docs = await getClientDocuments(found.id);
          if (mountedRef.current) {
            setClientDocuments(docs);
            setDocumentsError(null);
          }
        } catch (docErr) {
          console.error('❌ [V6.0] Erro ao carregar documentos iniciais:', docErr.message);
          if (mountedRef.current) setDocumentsError(docErr.message || 'Erro ao carregar documentos');
        } finally {
          if (mountedRef.current) setDocumentsLoading(false);
        }

      } catch (error) {
        console.error("❌ Erro em ClientDetails:", error);
        if (mountedRef.current) {
          setLoadError(error.message || 'Erro ao carregar cliente');
          showToast('Erro ao carregar cliente', 'error');
          addNotification({
            type: 'error',
            title: 'Erro ao carregar',
            message: error.message || 'Tente novamente mais tarde.',
            metadata: { clientId: id }
          });
        }
      } finally {
        if (mountedRef.current) setLoading(false);
      }
    };

    loadClientData();
  }, [ctxReady, id]); // eslint-disable-line react-hooks/exhaustive-deps

  // ==========================================================================
  // 🔥 V6.0: OBSERVER REAL DE DATACONTEXT — ctx._listeners (recibe state completo)
  // Reemplaza por completo el polling manual (documentsKey/handleDocumentChange)
  // y la API ficticia addObserver/removeObserver. DataContext expone un array
  // _listeners donde cada listener recibe el STATE COMPLETO (no un evento con
  // .collection). Comparamos contra el state anterior para saber qué cambió.
  // ==========================================================================
  useEffect(() => {
    if (!client?.id || !dataContextRef.current) return;
    const ctx = dataContextRef.current;
    let prevState = null;

    const observer = (newState) => {
      if (!mountedRef.current || !clientRef.current?.id) return;
      try {
        if (prevState) {
          const docsChanged = JSON.stringify(prevState.documents) !== JSON.stringify(newState.documents);
          const clientsChanged = JSON.stringify(prevState.clients) !== JSON.stringify(newState.clients);
          const propsChanged = JSON.stringify(prevState.properties) !== JSON.stringify(newState.properties);

          if (docsChanged) {
            console.log('🔄 [V6.0] Observer: documentos cambiaron');
            refreshClientDocuments();
          }
          if (clientsChanged || propsChanged) {
            console.log('🔄 [V6.0] Observer: clientes/propiedades cambiaron');
            refreshLinkedProperties();
          }
        }
        prevState = newState;
      } catch (e) {
        console.error('❌ [V6.0] Error en observer:', e.message);
      }
    };

    if (Array.isArray(ctx._listeners)) {
      ctx._listeners.push(observer);
      console.log('🔗 [V6.0] Observer registrado en DataContext via _listeners');

      observerCleanupRef.current = () => {
        ctx._listeners = ctx._listeners.filter((l) => l !== observer);
        console.log('🧹 [V6.0] Observer removido');
      };
    } else {
      console.warn('⚠️ [V6.0] ctx._listeners no disponible');
    }

    return () => {
      if (observerCleanupRef.current) {
        observerCleanupRef.current();
        observerCleanupRef.current = null;
      }
    };
  }, [client?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // ==========================================================================
  // 🔥 V6.0: HANDLERS (con DataContext async + mountedRef + timeout)
  // ==========================================================================
  const phoneDigits = useMemo(() => onlyDigits(client?.phone), [client?.phone]);

  const waLink = useMemo(() => {
    if (!phoneDigits) return "";
    const hasCountryCode = phoneDigits.length > 11 || phoneDigits.startsWith("55");
    return hasCountryCode
      ? `https://wa.me/${phoneDigits.replace('+', '')}`
      : `https://wa.me/55${phoneDigits}`;
  }, [phoneDigits]);

  const mailtoLink = useMemo(() => {
    const email = safeStr(client?.email, "");
    if (!email) return "";
    const subject = encodeURIComponent(`Olá, ${safeStr(client?.name, "")}`);
    return `mailto:${email}?subject=${subject}`;
  }, [client?.email, client?.name]);

  const lastContactInfo = useMemo(() => {
    if (!client) return null;

    const dates = [
      client.lastContactAt,
      ...(client.notes?.map((n) => n.createdAt) || []),
      ...(client.messages?.map((m) => m.createdAt) || [])
    ].filter(Boolean);

    if (dates.length === 0) return null;

    const lastDate = new Date(Math.max(...dates.map((d) => new Date(d))));
    return {
      date: lastDate,
      formatted: formatLastContact(lastDate)
    };
  }, [client]);

  const openWhatsApp = useCallback(() => {
    triggerHaptic(10);
    if (!waLink) {
      showToast('Cadastre o telefone do cliente para abrir o WhatsApp', 'info');
      return;
    }
    window.open(waLink, "_blank", "noopener,noreferrer");
  }, [waLink, showToast]);

  const callPhone = useCallback(() => {
    triggerHaptic(10);
    const phone = safeStr(client?.phone, "");
    if (!phone) {
      showToast('Cadastre o telefone do cliente para ligar', 'info');
      return;
    }
    window.open(`tel:${phone}`, "_self");
  }, [client?.phone, showToast]);

  const copyToClipboard = useCallback(async (text, label = "Copiado!") => {
    try {
      await navigator.clipboard.writeText(text);
      triggerHaptic(15);
      showToast(label, 'success');
    } catch {
      showToast('Não foi possível copiar automaticamente', 'warning');
    }
  }, [showToast]);

  // Refrescar propiedades vinculadas (async, desde DataContext) — lógica intacta
  const refreshLinkedProperties = useCallback(async () => {
    const currentClient = clientRef.current;
    if (!currentClient?.id) return;

    const props = await getProperties();
    const propsArray = Array.isArray(props) ? props : [];
    const linked = propsArray.filter((p) =>
      p.clientId === currentClient.id ||
      (p.linkedClients && p.linkedClients.includes(currentClient.id))
    );

    const withPreviews = await Promise.all(
      linked.map(async (prop) => {
        const preview = await getPropertyPreview(prop);
        return { ...prop, previewImage: preview };
      })
    );

    if (mountedRef.current) setLinkedProperties(withPreviews);

    const updatedClient = await getClientById(currentClient.id);
    if (updatedClient && mountedRef.current) {
      setClient({
        ...updatedClient,
        tags: updatedClient.tags || []
      });
    }

    if (mountedRef.current) {
      setPropertiesCache((prev) => ({ ...prev, timestamp: null }));
    }
  }, [getProperties, getClientById, getPropertyPreview]);

  // Cargar propiedades disponibles CON DIAGNÓSTICO — lógica intacta
  const loadAvailablePropertiesWithPreviews = useCallback(async () => {
    const now = Date.now();

    if (propertiesCache.timestamp && (now - propertiesCache.timestamp) < PROPERTIES_CACHE_TTL) {
      setAvailableProperties(propertiesCache.available);
      setShowLinkModal(true);
      setModalSearchTerm('');
      setModalDiagnostic(null);
      return;
    }

    setLoadingPreviews(true);
    setModalDiagnostic(null);

    try {
      const props = await getProperties();
      console.log('📊 [V6.0] Total propiedades obtenidas:', props.length);

      const withClient = props.filter((p) => !!p.clientId);
      const withoutClient = props.filter((p) => !p.clientId);
      const soldOrUnavailable = props.filter((p) =>
        p.status === 'Vendido' || p.status === 'Indisponível' || p.status === 'Alugado'
      );

      const diagnosticInfo = {
        total: props.length,
        conCliente: withClient.length,
        sinCliente: withoutClient.length,
        vendidasOIndisponibles: soldOrUnavailable.length,
        disponiblesReales: withoutClient.filter((p) =>
          p.status !== 'Vendido' && p.status !== 'Indisponível' && p.status !== 'Alugado'
        ).length
      };

      console.log('📊 [V6.0] Diagnóstico:', diagnosticInfo);
      if (mountedRef.current) setModalDiagnostic(diagnosticInfo);

      const available = props.filter((p) =>
        !p.clientId &&
        p.status !== 'Vendido' &&
        p.status !== 'Indisponível' &&
        p.status !== 'Alugado'
      );

      console.log('📊 [V6.0] Propiedades disponibles para vincular:', available.length);

      const withPreviews = await Promise.all(
        available.map(async (prop) => {
          const preview = await getPropertyPreview(prop);
          return { ...prop, previewImage: preview };
        })
      );

      if (mountedRef.current) {
        setPropertiesCache({
          timestamp: now,
          available: withPreviews
        });
        setAvailableProperties(withPreviews);
      }
    } catch (error) {
      console.error('❌ [V6.0] Erro ao carregar previews:', error);
      try {
        const props = await getProperties();
        const available = props.filter((p) =>
          !p.clientId &&
          p.status !== 'Vendido' &&
          p.status !== 'Indisponível'
        );
        if (mountedRef.current) setAvailableProperties(available);
      } catch {
        /* se o fallback também falhar, a modal simplesmente mostra lista vazia */
      }
    } finally {
      if (mountedRef.current) {
        setLoadingPreviews(false);
        setShowLinkModal(true);
        setModalSearchTerm('');
      }
    }
  }, [getProperties, getPropertyPreview, propertiesCache.timestamp]);

  const filteredAvailableProperties = useMemo(() => {
    if (!modalSearchTerm) return availableProperties;

    const term = modalSearchTerm.toLowerCase();
    return availableProperties.filter((p) =>
      p.title?.toLowerCase().includes(term) ||
      p.neighborhood?.toLowerCase().includes(term) ||
      p.city?.toLowerCase().includes(term) ||
      p.address?.toLowerCase().includes(term)
    );
  }, [availableProperties, modalSearchTerm]);

  // Vincular propiedad (DataContext async + tags persistentes) — lógica intacta
  const handleLinkProperty = useCallback(async (propertyId) => {
    try {
      const ctx = await getDataContext();
      const currentClient = clientRef.current;

      if (!ctx?.collections?.properties?.update) {
        showToast('Sistema de sincronização não disponível', 'error');
        return;
      }

      await withTimeout(
        ctx.collections.properties.update(propertyId, {
          clientId: currentClient.id,
          linkedClients: [...(currentClient.linkedClients || []), currentClient.id],
          updatedAt: new Date().toISOString()
        }),
        ASYNC_TIMEOUT_MS,
        'vinculação de imóvel'
      );

      console.log('✅ [V6.0] Propiedad vinculada:', propertyId, '→ cliente:', currentClient.id);

      await refreshLinkedProperties();

      if (mountedRef.current) setShowLinkModal(false);

      const property = availableProperties.find((p) => p.id === propertyId);
      showToast('✅ Imóvel vinculado com sucesso!', 'success');

      addNotification({
        type: 'success',
        title: 'Imóvel vinculado',
        message: `${property?.title || 'Imóvel'} vinculado a ${currentClient.name}`,
        metadata: { propertyId, clientId: currentClient.id }
      });

    } catch (e) {
      console.error('❌ Erro ao vincular imóvel:', e);
      showToast('Erro ao vincular imóvel', 'error');
    }
  }, [getDataContext, refreshLinkedProperties, availableProperties, showToast, addNotification]);

  // Desvincular propiedad (DataContext async) — lógica intacta
  const handleUnlinkProperty = useCallback((propertyId) => {
    const currentClient = clientRef.current;

    openConfirm({
      title: "Desvincular imóvel",
      message: "Deseja realmente remover o vínculo deste imóvel?",
      type: "warning",
      onConfirm: async () => {
        try {
          const ctx = await getDataContext();
          if (!ctx?.collections?.properties?.update) {
            showToast('Sistema de sincronização não disponível', 'error');
            return;
          }

          await withTimeout(
            ctx.collections.properties.update(propertyId, {
              clientId: null,
              linkedClients: [],
              updatedAt: new Date().toISOString()
            }),
            ASYNC_TIMEOUT_MS,
            'desvinculação de imóvel'
          );

          await refreshLinkedProperties();

          showToast('✅ Vínculo removido com sucesso!', 'success');

          addNotification({
            type: 'success',
            title: 'Imóvel desvinculado',
            message: 'Imóvel removido do cliente',
            metadata: { propertyId, clientId: currentClient.id }
          });
        } catch (e) {
          console.error('❌ Erro ao desvincular imóvel:', e);
          showToast('Erro ao desvincular imóvel', 'error');
        }
      },
    });
  }, [getDataContext, refreshLinkedProperties, openConfirm, showToast, addNotification]);

  // Eliminar cliente (DataContext async) — lógica intacta
  const handleDelete = useCallback(() => {
    const currentClient = clientRef.current;

    openConfirm({
      title: "Excluir cliente",
      message: `Tem certeza que deseja excluir "${safeStr(currentClient?.name)}"?`,
      type: "danger",
      onConfirm: async () => {
        try {
          const ctx = await getDataContext();
          if (!ctx?.collections?.clients?.delete) {
            showToast('Sistema de sincronização não disponível', 'error');
            return;
          }

          await withTimeout(
            ctx.collections.clients.delete(currentClient.id),
            ASYNC_TIMEOUT_MS,
            'exclusão de cliente'
          );

          showToast('✅ Cliente excluído com sucesso!', 'success');

          addNotification({
            type: 'success',
            title: 'Cliente excluído',
            message: `${currentClient.name} foi removido do sistema`,
            metadata: { clientId: currentClient.id }
          });

          navigate("/clientes");
        } catch (e) {
          console.error('❌ Erro ao excluir cliente:', e);
          showToast('Erro ao excluir cliente', 'error');
        }
      },
    });
  }, [getDataContext, navigate, openConfirm, showToast, addNotification]);

  // PERSISTIR TAGS — DOBLE PERSISTENCIA (DataContext + database.js) — lógica intacta
  const handleTagsChange = useCallback(async (newTags) => {
    const currentClient = clientRef.current;
    if (!currentClient?.id) return;

    setClient((prev) => {
      if (!prev) return prev;
      return { ...prev, tags: newTags };
    });

    console.log('🏷️ [V6.0] Tags actualizados (optimistic):', newTags);

    const persistPromises = [];

    try {
      const ctx = await getDataContext();
      if (ctx?.collections?.clients?.update) {
        persistPromises.push(
          ctx.collections.clients.update(currentClient.id, {
            tags: newTags,
            updatedAt: new Date().toISOString()
          }).then(() => console.log('✅ [V6.0] Tags persistidos en DataContext'))
        );
      }
    } catch (e) {
      console.warn('⚠️ [V6.0] DataContext no disponible para tags');
    }

    if (database?.updateClient) {
      persistPromises.push(
        database.updateClient(currentClient.id, {
          tags: newTags,
          updatedAt: new Date().toISOString()
        }).then(() => console.log('✅ [V6.0] Tags persistidos en database.js (fallback)'))
      );
    }

    try {
      const key = `client_tags_${currentClient.id}`;
      localStorage.setItem(key, JSON.stringify(newTags));
      console.log('✅ [V6.0] Tags guardados en localStorage (respaldo)');
    } catch (lsError) {
      console.warn('⚠️ [V6.0] No se pudo guardar en localStorage');
    }

    try {
      await Promise.allSettled(persistPromises);
      console.log('✅ [V6.0] Tags persistidos en todas las capas');
    } catch (e) {
      console.error('❌ [V6.0] Error al persistir tags:', e);
    }
  }, [getDataContext, database]);

  // 🔥 V6.0: Documentos — ya NO usa documentsKey. El observer es la fuente de
  // verdad; este callback queda como hook opcional para components hijos que
  // aún disparen un evento local justo después de subir/eliminar (UX más
  // instantánea mientras el observer confirma desde el backend).
  const handleDocumentChange = useCallback(() => {
    console.log('📎 [V6.0] DocumentList notificó un cambio local — refrescando de inmediato');
    refreshClientDocuments();
  }, [refreshClientDocuments]);

  const retryLoadClient = useCallback(() => {
    setCtxReady(false);
    setTimeout(() => {
      if (mountedRef.current) setCtxReady(true);
    }, 50);
  }, []);

  // Exportar PDF — lógica intacta
  const exportClientPDF = useCallback(async () => {
    const currentClient = clientRef.current;

    try {
      const jsPDF = (await import('jspdf')).default;

      const doc = new jsPDF("p", "mm", "a4");
      const pageWidth = doc.internal.pageSize.getWidth();
      doc.setFillColor(primaryColor);
      doc.rect(0, 0, pageWidth, 40, 'F');

      doc.setTextColor(255, 255, 255);
      doc.setFont("helvetica", "bold");
      doc.setFontSize(24);
      doc.text(company.nome, 14, 25);
      doc.setFontSize(10);
      doc.text(`Relatório de Cliente - ${formatDateBR(new Date())}`, 14, 32);

      let y = 55;
      doc.setTextColor(30, 41, 59);
      doc.setFontSize(16);
      doc.text(currentClient?.name || "Cliente", 14, y);
      y += 10;

      const fields = [
        ["Tipo", currentClient?.type],
        ["Status", currentClient?.status],
        ["Pipeline", currentClient?.pipeline],
        ["Temperatura", currentClient?.temperature],
        ["Score", `${currentClient?.score || 0}%`],
        ["Telefone", currentClient?.phone],
        ["Email", currentClient?.email],
        ["Tags", (currentClient?.tags || []).join(", ")],
      ];

      fields.forEach(([label, value]) => {
        if (value) {
          doc.setFont("helvetica", "bold");
          doc.text(`${label}:`, 14, y);
          doc.setFont("helvetica", "normal");
          doc.text(String(value), 60, y);
          y += 7;
        }
      });

      doc.save(`cliente-${currentClient?.name?.replace(/\s+/g, '_')}.pdf`);
      showToast('📄 PDF gerado com sucesso!', 'success');

      addNotification({
        type: 'success',
        title: 'PDF exportado',
        message: `Relatório do cliente ${currentClient.name}`,
        metadata: { clientId: currentClient.id }
      });

    } catch (e) {
      console.error(e);
      showToast('Erro ao gerar PDF', 'error');
    }
  }, [company, primaryColor, showToast, addNotification]);

  useClientDetailsShortcuts({
    onEdit: () => navigate(`/clientes/editar/${client?.id}`),
    onDelete: handleDelete,
    onWhatsApp: openWhatsApp,
    onEmail: () => mailtoLink && window.open(mailtoLink, "_self"),
    onCall: callPhone,
    onNewNote: () => {
      if (!isMobile) {
        const noteInput = document.querySelector('textarea[placeholder*="Adicionar nota"]');
        noteInput?.focus();
      }
    },
    onNewTask: () => navigate(`/tarefas/nova?cliente=${client?.id}`),
    onExport: exportClientPDF
  }, [client?.id, mailtoLink, handleDelete, openWhatsApp, callPhone, exportClientPDF, isMobile]);

  // Tab activa con feedback háptico en mobile
  const handleTabChange = useCallback((tabId) => {
    if (isMobile) triggerHaptic(8);
    setActiveTab(tabId);
  }, [isMobile]);

  // ==========================================================================
  // 🎨 RENDER
  // ==========================================================================

  if (loadError && !client) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-white dark:from-slate-950 dark:to-slate-900 p-6">
        <div className="max-w-7xl mx-auto">
          <ErrorState message={loadError} onRetry={retryLoadClient} />
        </div>
      </div>
    );
  }

  if (loading || !client) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-white dark:from-slate-950 dark:to-slate-900 p-6">
        <div className="max-w-7xl mx-auto">
          <ShimmerLoader />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-white dark:from-slate-950 dark:to-slate-900 transition-colors duration-300">
      <style>{`
        @keyframes shimmer {
          100% { transform: translateX(100%); }
        }
      `}</style>

      <div
        className={`max-w-7xl mx-auto ${isMobile ? 'p-3 space-y-4 pb-24' : 'p-6 space-y-6'}`}
        ref={contentRef}
      >

        {/* BREADCRUMB (desktop apenas) — layout editorial premium */}
        {!isMobile && (
          <motion.nav
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="flex items-center gap-1.5 text-sm text-muted dark:text-muted"
            aria-label="breadcrumb"
          >
            <button
              onClick={() => navigate('/clientes')}
              className="hover:text-primary dark:hover:text-white transition-colors"
            >
              Clientes
            </button>
            <ChevronRight className="w-3.5 h-3.5" />
            <span className="text-primary dark:text-white font-medium truncate max-w-[240px]">
              {safeStr(client.name)}
            </span>
          </motion.nav>
        )}

        {/* HEADER PREMIUM — glassmorphism sutil, dark-mode full */}
        <motion.div
          ref={headerRef}
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          style={{ willChange: 'transform, opacity' }}
          className={`sticky top-0 z-10 bg-card/80 dark:bg-slate-900/70 backdrop-blur-md border-b border-color/60 dark:border-slate-700/50 -mx-6 px-4 py-2 mb-4 ${
            isMobile ? '-mx-3 px-3' : 'px-6 py-3'
          }`}
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <motion.button
                whileHover={{ x: -4 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => navigate("/clientes")}
                className="flex items-center gap-1 text-secondary dark:text-slate-300 hover:text-[var(--primary-color)] transition-colors group"
              >
                <ArrowLeft className={`${isMobile ? 'w-4 h-4' : 'w-5 h-5'} group-hover:-translate-x-1 transition-transform`} />
                <span className={`font-medium ${isMobile ? 'text-sm' : ''}`}>Voltar</span>
                {!isMobile && (
                  <kbd className="ml-2 px-2 py-0.5 text-xs bg-muted dark:bg-slate-800 text-muted dark:text-muted rounded-md">ESC</kbd>
                )}
              </motion.button>
            </div>

            {!isMobile && (
              <div className="flex items-center gap-1">
                <AnimatePresence>
                  {showTips && (
                    <motion.div
                      initial={{ opacity: 0, x: 20 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: 20 }}
                      className="hidden lg:flex items-center gap-2 px-3 py-1.5 bg-blue-50 dark:bg-blue-500/10 text-blue-700 dark:text-blue-300 rounded-lg text-sm"
                    >
                      <HelpCircle className="w-4 h-4" />
                      <span>⌘E • ⌘W • ⌘D</span>
                      <button
                        onClick={() => setShowTips(false)}
                        className="ml-2 p-0.5 hover:bg-blue-100 dark:hover:bg-blue-500/20 rounded"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </motion.div>
                  )}
                </AnimatePresence>

                <Tooltip content="Editar cliente (⌘E)">
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={() => navigate(`/clientes/editar/${client.id}`)}
                    className="rounded-xl bg-slate-900 hover:bg-slate-800 dark:bg-card dark:hover:bg-muted text-white dark:text-primary flex items-center gap-1 shadow-lg shadow-slate-200 dark:shadow-none px-3 py-2"
                  >
                    <Edit className="w-4 h-4" />
                    <span>Editar</span>
                  </motion.button>
                </Tooltip>

                <Tooltip content="Exportar PDF (⌘⇧S)">
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={exportClientPDF}
                    className="rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white flex items-center gap-1 shadow-lg shadow-emerald-200 dark:shadow-emerald-900/30 px-3 py-2"
                  >
                    <Download className="w-4 h-4" />
                    <span>PDF</span>
                  </motion.button>
                </Tooltip>

                <Tooltip content="Excluir cliente (⌘D)">
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={handleDelete}
                    className="rounded-xl bg-rose-600 hover:bg-rose-700 text-white flex items-center gap-1 shadow-lg shadow-rose-200 dark:shadow-rose-900/30 px-3 py-2"
                  >
                    <Trash2 className="w-4 h-4" />
                    <span>Excluir</span>
                  </motion.button>
                </Tooltip>
              </div>
            )}

            {/* Mobile: ações rápidas compactas no header (estilo app nativo) */}
            {isMobile && (
              <div className="flex items-center gap-1.5">
                <motion.button
                  whileTap={{ scale: 0.9 }}
                  onClick={() => { triggerHaptic(8); navigate(`/clientes/editar/${client.id}`); }}
                  className="p-2 rounded-full bg-slate-900 dark:bg-card text-white dark:text-primary active:opacity-80"
                  aria-label="Editar cliente"
                >
                  <Edit className="w-4 h-4" />
                </motion.button>
                <motion.button
                  whileTap={{ scale: 0.9 }}
                  onClick={() => { triggerHaptic(8); handleDelete(); }}
                  className="p-2 rounded-full bg-rose-600 text-white active:opacity-80"
                  aria-label="Excluir cliente"
                >
                  <Trash2 className="w-4 h-4" />
                </motion.button>
              </div>
            )}
          </div>
        </motion.div>

        {/* CARD DO CLIENTE PREMIUM — título ultra legível em claro/escuro */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          style={{ willChange: 'transform, opacity' }}
          className="relative bg-card dark:bg-slate-900/60 rounded-2xl shadow-lg dark:shadow-black/20 border border-color dark:border-slate-700/60 overflow-hidden group"
        >
          <div
            className="absolute top-0 left-0 right-0 h-1"
            style={{
              background: `linear-gradient(90deg, ${primaryColor}, ${primaryColor}dd, ${primaryColor}aa)`
            }}
          />

          <div className={`${isMobile ? 'p-4' : 'p-6'}`}>
            <div className={`flex ${isMobile ? 'flex-col' : 'flex-col lg:flex-row lg:items-start'} justify-between gap-4`}>
              <div className="w-full lg:flex-1 space-y-3">
                <div className="flex items-start gap-3">
                  {/* Avatar com iniciais — usa a cor white-label */}
                  <div
                    className={`flex-shrink-0 rounded-2xl flex items-center justify-center font-bold text-white shadow-md ${
                      isMobile ? 'w-11 h-11 text-sm' : 'w-14 h-14 text-lg'
                    }`}
                    style={{ backgroundColor: primaryColor }}
                  >
                    {getInitials(client.name)}
                  </div>

                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      {/* 🎯 TÍTULO ULTRA PERFEITO — cores do sistema, sem hardcode */}
                      <h1
                        className={`font-bold text-primary dark:text-white truncate ${
                          isMobile ? 'text-xl' : 'text-2xl sm:text-3xl'
                        }`}
                      >
                        {safeStr(client.name)}
                      </h1>

                      {lastContactInfo && !isMobile && (
                        <motion.span
                          initial={{ scale: 0.9 }}
                          animate={{ scale: 1 }}
                          className="flex items-center gap-1 px-3 py-1 bg-muted dark:bg-slate-800 rounded-full text-xs text-secondary dark:text-slate-300 border border-color dark:border-slate-700 whitespace-nowrap"
                        >
                          <Clock className="w-3 h-3" />
                          Último: {lastContactInfo.formatted}
                        </motion.span>
                      )}
                    </div>
                    {client.phone && (
                      <p className="text-sm text-muted dark:text-muted mt-0.5 truncate">
                        {client.phone}
                      </p>
                    )}
                  </div>
                </div>

                <div className="max-w-md">
                  <ScoreProgress
                    score={client.score || 0}
                    size={isMobile ? "md" : "lg"}
                    showMessage={!isMobile}
                    animated={true}
                  />
                </div>

                {/* SmartTags com persistência garantida (dupla camada) — lógica intacta */}
                <div className="mt-3">
                  <SmartTags
                    key={`tags-${client.id}-${(client.tags || []).join(',')}`}
                    clientId={client.id}
                    showSuggestions={!isMobile}
                    showCategories={!isMobile}
                    size={isMobile ? "sm" : "md"}
                    onTagsChange={handleTagsChange}
                    initialTags={client.tags || []}
                  />
                </div>
              </div>

              <div className={`flex ${isMobile ? 'flex-row flex-wrap gap-2' : 'lg:flex-col gap-2'}`}>
                <motion.div
                  whileHover={{ scale: isMobile ? 1 : 1.02 }}
                  className={`flex items-center gap-2 px-3 py-2 rounded-xl border ${getPipelineColor(client.pipeline)} ${
                    isMobile ? 'flex-1 min-w-[120px]' : ''
                  }`}
                >
                  <Target className={`${isMobile ? 'w-3 h-3' : 'w-4 h-4'}`} />
                  <div>
                    <div className={`opacity-70 ${isMobile ? 'text-[10px]' : 'text-xs'}`}>Pipeline</div>
                    <div className={`font-medium ${isMobile ? 'text-xs' : ''}`}>
                      {isMobile ? (client.pipeline === "Lead Novo" ? "Lead" : client.pipeline) : safeStr(client.pipeline)}
                    </div>
                  </div>
                </motion.div>

                <motion.div
                  whileHover={{ scale: isMobile ? 1 : 1.02 }}
                  className={`flex items-center gap-2 px-3 py-2 rounded-xl border ${getTemperatureColor(client.temperature)} ${
                    isMobile ? 'flex-1 min-w-[120px]' : ''
                  }`}
                >
                  <Thermometer className={`${isMobile ? 'w-3 h-3' : 'w-4 h-4'}`} />
                  <div>
                    <div className={`opacity-70 ${isMobile ? 'text-[10px]' : 'text-xs'}`}>Temp.</div>
                    <div className={`font-medium ${isMobile ? 'text-xs' : ''}`}>
                      {isMobile ? client.temperature?.charAt(0) : safeStr(client.temperature)}
                    </div>
                  </div>
                </motion.div>

                <motion.div
                  whileHover={{ scale: isMobile ? 1 : 1.02 }}
                  className={`flex items-center gap-2 px-3 py-2 rounded-xl border bg-muted dark:bg-slate-800/60 border-color dark:border-slate-700 text-secondary dark:text-slate-200 ${
                    isMobile ? 'flex-1 min-w-[120px]' : ''
                  }`}
                >
                  <Tag className={`${isMobile ? 'w-3 h-3' : 'w-4 h-4'}`} />
                  <div>
                    <div className={`opacity-70 ${isMobile ? 'text-[10px]' : 'text-xs'}`}>Tipo</div>
                    <div className={`font-medium ${isMobile ? 'text-xs' : ''}`}>
                      {isMobile ? client.type?.charAt(0) : safeStr(client.type, "Cliente")}
                    </div>
                  </div>
                </motion.div>
              </div>
            </div>

            {/* Ações rápidas — botões touch-friendly em mobile */}
            <div className={`flex flex-wrap gap-2 mt-4 pt-4 border-t border-color dark:border-slate-800 ${isMobile ? 'gap-1.5' : ''}`}>
              <Tooltip content="Abrir WhatsApp (⌘W)">
                <motion.button
                  whileHover={{ scale: isMobile ? 1 : 1.02 }}
                  whileTap={{ scale: 0.96 }}
                  onClick={openWhatsApp}
                  className={`bg-green-600 hover:bg-green-700 text-white rounded-xl flex items-center gap-1 shadow-lg shadow-green-200 dark:shadow-green-900/30 ${
                    isMobile ? 'px-3 py-2.5 text-xs flex-1 justify-center min-h-[44px]' : 'px-4 py-2'
                  }`}
                >
                  <MessageCircle className={isMobile ? 'w-4 h-4' : 'w-4 h-4'} />
                  <span>{isMobile ? 'Whats' : 'WhatsApp'}</span>
                </motion.button>
              </Tooltip>

              <Tooltip content="Ligar (⌥⌘C)">
                <motion.button
                  whileHover={{ scale: isMobile ? 1 : 1.02 }}
                  whileTap={{ scale: 0.96 }}
                  onClick={callPhone}
                  className={`bg-blue-600 hover:bg-blue-700 text-white rounded-xl flex items-center gap-1 shadow-lg shadow-blue-200 dark:shadow-blue-900/30 ${
                    isMobile ? 'px-3 py-2.5 text-xs flex-1 justify-center min-h-[44px]' : 'px-4 py-2'
                  }`}
                >
                  <Phone className={isMobile ? 'w-4 h-4' : 'w-4 h-4'} />
                  <span>Ligar</span>
                </motion.button>
              </Tooltip>

              <Tooltip content="Enviar Email (⌘⇧E)">
                <motion.button
                  whileHover={{ scale: isMobile ? 1 : 1.02 }}
                  whileTap={{ scale: 0.96 }}
                  onClick={() => mailtoLink && window.open(mailtoLink, "_self")}
                  className={`bg-slate-800 hover:bg-slate-900 dark:bg-slate-700 dark:hover:bg-slate-600 text-white rounded-xl flex items-center gap-1 shadow-lg shadow-slate-200 dark:shadow-none ${
                    isMobile ? 'px-3 py-2.5 text-xs flex-1 justify-center min-h-[44px]' : 'px-4 py-2'
                  }`}
                >
                  <Mail className={isMobile ? 'w-4 h-4' : 'w-4 h-4'} />
                  <span>Email</span>
                </motion.button>
              </Tooltip>

              <Tooltip content="Copiar telefone">
                <motion.button
                  whileHover={{ scale: isMobile ? 1 : 1.02 }}
                  whileTap={{ scale: 0.96 }}
                  onClick={() => copyToClipboard(safeStr(client.phone, ""), "Telefone copiado")}
                  className={`bg-muted hover:bg-muted dark:bg-slate-800 dark:hover:bg-slate-700 text-primary dark:text-slate-100 rounded-xl flex items-center gap-1 border border-color dark:border-slate-700 ${
                    isMobile ? 'px-3 py-2.5 text-xs flex-1 justify-center min-h-[44px]' : 'px-4 py-2'
                  }`}
                >
                  <Copy className={isMobile ? 'w-4 h-4' : 'w-4 h-4'} />
                  <span>Copiar</span>
                </motion.button>
              </Tooltip>
            </div>

            {isMobile && lastContactInfo && (
              <div className="mt-3 flex items-center gap-1 text-xs text-muted dark:text-muted border-t border-color dark:border-slate-800 pt-3">
                <Clock className="w-3 h-3" />
                Último contato: {lastContactInfo.formatted}
              </div>
            )}
          </div>
        </motion.div>

        {/* TABS DESKTOP — indicador animado */}
        {!isMobile && (
          <div className="relative border-b border-color dark:border-slate-800">
            <div className="flex gap-1 overflow-x-auto pb-px scrollbar-hide">
              {TABS.map((tab) => {
                const Icon = tab.icon;
                const isActive = activeTab === tab.id;

                return (
                  <motion.button
                    key={tab.id}
                    whileHover={{ y: -1 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={() => handleTabChange(tab.id)}
                    className="relative px-3 py-2 text-sm font-medium group outline-none"
                  >
                    <span
                      className={`flex items-center gap-1 px-2 py-1 rounded-lg transition-colors ${
                        isActive
                          ? 'text-[var(--primary-color)]'
                          : 'text-muted dark:text-muted group-hover:text-secondary dark:group-hover:text-slate-200'
                      }`}
                    >
                      <Icon className="w-4 h-4" />
                      {tab.label}
                      {tab.id === 'documents' && clientDocuments.length > 0 && (
                        <span className="ml-0.5 text-[10px] px-1.5 py-0.5 rounded-full bg-muted dark:bg-slate-800 text-muted dark:text-muted">
                          {clientDocuments.length}
                        </span>
                      )}
                    </span>

                    {isActive && (
                      <motion.div
                        layoutId="activeTab"
                        className="absolute bottom-0 left-0 right-0 h-0.5"
                        style={{ backgroundColor: primaryColor }}
                        transition={{ type: "spring", stiffness: 500, damping: 30 }}
                      />
                    )}
                  </motion.button>
                );
              })}
            </div>
          </div>
        )}

        {/* CONTEÚDO DAS TABS */}
        <motion.div
          key={activeTab}
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.2 }}
          style={{ willChange: 'transform, opacity' }}
          className="space-y-4"
        >
          {activeTab === 'timeline' && (
            <TabErrorBoundary tabName="timeline">
              <Timeline
                clientId={client.id}
                showFilters={!isMobile}
                showSearch={!isMobile}
                showQuickPeriods={!isMobile}
                maxHeight={isMobile ? "400px" : "600px"}
              />
            </TabErrorBoundary>
          )}

          {activeTab === 'notes' && (
            <TabErrorBoundary tabName="notas">
              <ClientNotes
                clientId={client.id}
                onUpdate={async () => {
                  const updated = await getClientById(client.id);
                  if (updated && mountedRef.current) setClient({ ...updated, tags: updated.tags || [] });
                }}
              />
            </TabErrorBoundary>
          )}

          {activeTab === 'documents' && (
            <TabErrorBoundary tabName="documentos">
              {documentsError ? (
                <ErrorState message={documentsError} onRetry={refreshClientDocuments} />
              ) : (
                <DocumentList
                  clientId={client.id}
                  documents={clientDocuments}
                  loading={documentsLoading}
                  showUpload={true}
                  showStats={!isMobile}
                  allowDelete={true}
                  allowDownload={true}
                  onDocumentChange={handleDocumentChange}
                />
              )}
            </TabErrorBoundary>
          )}

          {activeTab === 'messages' && (
            <TabErrorBoundary tabName="mensagens">
              <ClientMessageHistory clientId={client.id} />
            </TabErrorBoundary>
          )}
        </motion.div>

        {/* IMÓVEIS DO CLIENTE — lógica de vinculação 100% intacta */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          style={{ willChange: 'transform, opacity' }}
          className="bg-card dark:bg-slate-900/60 border border-color dark:border-slate-700/60 rounded-2xl p-4 shadow-lg dark:shadow-black/20"
        >
          <div className="flex justify-between items-center gap-2 flex-wrap mb-4">
            <div className="flex items-center gap-2">
              <div
                className="p-2 rounded-xl"
                style={{ backgroundColor: primaryLight }}
              >
                <Home className={isMobile ? 'w-4 h-4' : 'w-5 h-5'} style={{ color: primaryColor }} />
              </div>
              <div>
                <h2 className={`font-semibold text-primary dark:text-white ${isMobile ? 'text-base' : 'text-xl'}`}>
                  Imóveis
                </h2>
                <p className={`text-muted dark:text-muted ${isMobile ? 'text-xs' : 'text-sm'}`}>
                  {linkedProperties.length} {linkedProperties.length === 1 ? 'vinculado' : 'vinculados'}
                </p>
              </div>
            </div>

            {/* BOTÃO VINCULAR — white-label visível, dark-mode compatível */}
            <motion.button
              whileHover={{ scale: isMobile ? 1 : 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={loadAvailablePropertiesWithPreviews}
              style={{
                backgroundColor: primaryColor,
                boxShadow: `0 10px 15px -3px ${primaryLight}`
              }}
              className={`text-white rounded-xl flex items-center gap-1 shadow-lg font-medium ${
                isMobile ? 'px-3 py-2 text-xs min-h-[40px]' : 'px-4 py-2'
              }`}
            >
              <Link className={isMobile ? 'w-3 h-3' : 'w-4 h-4'} />
              {isMobile ? 'Vincular' : 'Vincular Imóvel'}
            </motion.button>
          </div>

          {linkedProperties.length === 0 ? (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-center py-8 px-4"
            >
              <div
                className={`mx-auto mb-3 rounded-full flex items-center justify-center ${
                  isMobile ? 'w-16 h-16' : 'w-24 h-24'
                }`}
                style={{ backgroundColor: primaryLight }}
              >
                <Building2 className={isMobile ? 'w-6 h-6' : 'w-10 h-10'} style={{ color: primaryColor }} />
              </div>
              <h3 className={`font-semibold text-primary dark:text-white mb-1 ${isMobile ? 'text-sm' : 'text-lg'}`}>
                Nenhum imóvel vinculado
              </h3>
              <p className={`text-muted dark:text-muted max-w-sm mx-auto mb-4 ${isMobile ? 'text-xs' : ''}`}>
                {isMobile ? 'Vincule imóveis ao cliente' : 'Vincule imóveis a este cliente para acompanhar seus interesses e propostas.'}
              </p>
              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={loadAvailablePropertiesWithPreviews}
                style={{
                  backgroundColor: primaryColor,
                  boxShadow: `0 10px 15px -3px ${primaryLight}`
                }}
                className={`text-white rounded-xl inline-flex items-center gap-2 shadow-lg font-medium ${
                  isMobile ? 'px-4 py-2.5 text-xs min-h-[44px]' : 'px-6 py-3'
                }`}
              >
                <Plus className={isMobile ? 'w-3 h-3' : 'w-4 h-4'} />
                {isMobile ? 'Vincular' : 'Vincular Primeiro Imóvel'}
              </motion.button>
            </motion.div>
          ) : (
            <div className={`grid ${isMobile ? 'grid-cols-1 gap-3' : 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4'}`}>
              {linkedProperties.map((property, index) => (
                <PropertyCard
                  key={property.id}
                  property={property}
                  index={index}
                  companyCor={primaryColor}
                  onView={(propId) => navigate(`/admin/imoveis/${propId}`)}
                  onUnlink={handleUnlinkProperty}
                />
              ))}
            </div>
          )}
        </motion.div>

        {/* MODAL DE VINCULAÇÃO — lógica e diagnóstico 100% intactos. Renderizado via portal (V6.0) */}
        {portalize(linkModalMounted, (
          <AnimatePresence>
            {showLinkModal && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="fixed inset-0 bg-black/60 backdrop-blur-md flex items-center justify-center z-[99999] p-4"
                onClick={() => setShowLinkModal(false)}
              >
                <motion.div
                  initial={{ scale: 0.95, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.95, opacity: 0 }}
                onClick={(e) => e.stopPropagation()}
                className="bg-card dark:bg-slate-900 rounded-2xl shadow-2xl w-full max-w-2xl max-h-[80vh] overflow-hidden border border-transparent dark:border-slate-700/60"
              >
                <div className="px-4 py-3 border-b border-color dark:border-slate-800 flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div
                      className="p-1.5 rounded-lg"
                      style={{ backgroundColor: primaryLight }}
                    >
                      <Link className="w-4 h-4" style={{ color: primaryColor }} />
                    </div>
                    <div>
                      <h2 className={`font-semibold text-primary dark:text-white ${isMobile ? 'text-base' : 'text-xl'}`}>
                        Vincular Imóvel
                      </h2>
                      <p className={`text-muted dark:text-muted ${isMobile ? 'text-xs' : 'text-sm'}`}>
                        {loadingPreviews ? 'Carregando...' : `${availableProperties.length} disponíveis`}
                      </p>
                    </div>
                  </div>
                  <motion.button
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.9 }}
                    onClick={() => setShowLinkModal(false)}
                    className="p-1.5 hover:bg-muted dark:hover:bg-slate-800 rounded-lg transition-colors"
                  >
                    <X className="w-4 h-4 text-secondary dark:text-slate-300" />
                  </motion.button>
                </div>

                {modalDiagnostic && availableProperties.length === 0 && !loadingPreviews && (
                  <div className="mx-4 mt-3 p-3 bg-amber-50 dark:bg-amber-500/10 border border-amber-200 dark:border-amber-500/30 rounded-xl">
                    <div className="flex items-start gap-2">
                      <AlertCircle className="w-4 h-4 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" />
                      <div className="text-xs text-amber-800 dark:text-amber-200">
                        <p className="font-medium mb-1">Diagnóstico de propriedades:</p>
                        <ul className="space-y-0.5 text-amber-700 dark:text-amber-300">
                          <li>• Total no sistema: <strong>{modalDiagnostic.total}</strong></li>
                          <li>• Já vinculadas a clientes: <strong>{modalDiagnostic.conCliente}</strong></li>
                          <li>• Vendidas/Indisponíveis/Alugadas: <strong>{modalDiagnostic.vendidasOIndisponibles}</strong></li>
                          <li>• Disponíveis reais: <strong>{modalDiagnostic.disponiblesReales}</strong></li>
                        </ul>
                        {modalDiagnostic.disponiblesReales === 0 && (
                          <p className="mt-2 text-amber-800 dark:text-amber-200 font-medium">
                            Todas as propriedades já estão vinculadas ou indisponíveis. Crie um novo imóvel primeiro.
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                <div className="p-3 border-b border-color dark:border-slate-800">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted dark:text-muted" />
                    <input
                      type="text"
                      placeholder={isMobile ? "Buscar..." : "Buscar imóveis por endereço, bairro ou cidade..."}
                      className={`w-full pl-9 pr-4 py-2 border border-color dark:border-slate-700 dark:bg-slate-800 dark:text-white dark:placeholder:text-muted rounded-lg focus:ring-2 focus:outline-none ${
                        isMobile ? 'text-sm' : ''
                      }`}
                      style={{ '--tw-ring-color': primaryLight }}
                      value={modalSearchTerm}
                      onChange={(e) => setModalSearchTerm(e.target.value)}
                      disabled={loadingPreviews}
                    />
                    {modalSearchTerm && (
                      <button
                        onClick={() => setModalSearchTerm('')}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-muted hover:text-secondary dark:text-muted dark:hover:text-slate-300"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                </div>

                <div className={`overflow-y-auto ${isMobile ? 'p-3 max-h-[300px]' : 'p-4 max-h-[400px]'}`}>
                  {loadingPreviews ? (
                    <div className="flex items-center justify-center py-8">
                      <RefreshCw className="animate-spin w-6 h-6 text-muted dark:text-muted" />
                    </div>
                  ) : filteredAvailableProperties.length === 0 ? (
                    <div className="text-center py-8">
                      <Building2 className="w-8 h-8 text-slate-300 dark:text-secondary mx-auto mb-2" />
                      <p className="text-muted dark:text-muted text-sm">
                        {modalSearchTerm ? 'Nenhum imóvel encontrado com este termo' : 'Nenhum imóvel disponível para vincular'}
                      </p>
                      {modalSearchTerm && (
                        <button
                          onClick={() => setModalSearchTerm('')}
                          className="mt-2 text-xs text-[var(--primary-color)] hover:underline"
                        >
                          Limpar busca
                        </button>
                      )}
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {filteredAvailableProperties.map((property) => (
                        <motion.button
                          key={property.id}
                          whileHover={{ x: isMobile ? 2 : 4 }}
                          onClick={() => handleLinkProperty(property.id)}
                          className="w-full text-left border border-color dark:border-slate-700 p-3 rounded-xl hover:shadow-md dark:hover:bg-slate-800/60 transition-all bg-card dark:bg-slate-800/30 group"
                          disabled={loadingPreviews}
                        >
                          <div className="flex items-start gap-2">
                            <div className={`${isMobile ? 'w-12 h-12' : 'w-16 h-16'} bg-gradient-to-br from-slate-100 to-slate-200 dark:from-slate-700 dark:to-slate-800 rounded-lg overflow-hidden flex-shrink-0 relative`}>
                              {property.previewImage ? (
                                <img
                                  src={property.previewImage}
                                  alt={property.title}
                                  className="w-full h-full object-cover"
                                  loading="lazy"
                                />
                              ) : (
                                <div className="absolute inset-0 flex items-center justify-center">
                                  <Building2 className={`${isMobile ? 'w-4 h-4' : 'w-6 h-6'} text-muted dark:text-muted`} />
                                </div>
                              )}
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className={`font-medium text-primary dark:text-white truncate ${isMobile ? 'text-xs' : ''}`}>
                                {property.title || "Imóvel sem título"}
                              </div>
                              <div className={`text-muted dark:text-muted mt-0.5 truncate flex items-center gap-0.5 ${
                                isMobile ? 'text-[10px]' : 'text-xs'
                              }`}>
                                <MapPin className="w-2 h-2 flex-shrink-0" />
                                <span className="truncate">
                                  {property.neighborhood || 'Sem bairro'}
                                </span>
                              </div>
                              {property.price && (
                                <div className={`font-medium text-emerald-600 dark:text-emerald-400 mt-1 ${
                                  isMobile ? 'text-xs' : 'text-sm'
                                }`}>
                                  {new Intl.NumberFormat('pt-BR', {
                                    style: 'currency',
                                    currency: 'BRL',
                                    maximumFractionDigits: 0
                                  }).format(property.price)}
                                </div>
                              )}
                            </div>
                          </div>
                        </motion.button>
                      ))}
                    </div>
                  )}
                </div>

                <div className="px-4 py-3 border-t border-color dark:border-slate-800 bg-muted dark:bg-slate-900/80 flex justify-end">
                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={() => setShowLinkModal(false)}
                    className={`rounded-xl border border-color dark:border-slate-700 text-secondary dark:text-slate-200 hover:bg-card dark:hover:bg-slate-800 transition-colors ${
                      isMobile ? 'px-4 py-2 text-xs' : 'px-4 py-2'
                    }`}
                    disabled={loadingPreviews}
                  >
                    Fechar
                  </motion.button>
                </div>
              </motion.div>
            </motion.div>
          )}
          </AnimatePresence>
        ))}
      </div>

      {/* TABS MOBILE — navegação inferior tipo app nativa (iOS/Android) */}
      {isMobile && (
        <motion.nav
          initial={{ y: 80 }}
          animate={{ y: 0 }}
          transition={{ type: 'spring', stiffness: 300, damping: 30 }}
          style={{ willChange: 'transform' }}
          className="fixed bottom-0 left-0 right-0 z-20 bg-card/90 dark:bg-slate-900/90 backdrop-blur-lg border-t border-color dark:border-slate-800 pb-[env(safe-area-inset-bottom)]"
        >
          <div className="grid grid-cols-4">
            {TABS.map((tab) => {
              const Icon = tab.icon;
              const isActive = activeTab === tab.id;
              const badgeCount = tab.id === 'documents' ? clientDocuments.length : 0;

              return (
                <button
                  key={tab.id}
                  onClick={() => handleTabChange(tab.id)}
                  className="relative flex flex-col items-center justify-center gap-0.5 py-2 min-h-[56px] active:bg-muted dark:active:bg-slate-800 transition-colors"
                  aria-label={tab.label}
                  aria-current={isActive ? 'page' : undefined}
                >
                  <div className="relative">
                    <Icon
                      className="w-5 h-5 transition-colors"
                      style={{ color: isActive ? primaryColor : undefined }}
                      strokeWidth={isActive ? 2.4 : 2}
                    />
                    {badgeCount > 0 && (
                      <span
                        className="absolute -top-1.5 -right-2 min-w-[14px] h-[14px] px-0.5 rounded-full text-[9px] font-bold text-white flex items-center justify-center"
                        style={{ backgroundColor: primaryColor }}
                      >
                        {badgeCount > 9 ? '9+' : badgeCount}
                      </span>
                    )}
                  </div>
                  <span
                    className={`text-[10px] font-medium ${
                      isActive ? '' : 'text-muted dark:text-muted'
                    }`}
                    style={{ color: isActive ? primaryColor : undefined }}
                  >
                    {tab.mobileLabel}
                  </span>

                  {isActive && (
                    <motion.div
                      layoutId="activeTabMobile"
                      className="absolute top-0 left-1/2 -translate-x-1/2 w-8 h-0.5 rounded-full"
                      style={{ backgroundColor: primaryColor }}
                      transition={{ type: "spring", stiffness: 500, damping: 30 }}
                    />
                  )}
                </button>
              );
            })}
          </div>
        </motion.nav>
      )}
    </div>
  );
}